// Two qualifiers per group: unbeaten upper winner and lower-bracket winner.
export const isDotaTournament = (tour) => /dota/i.test(`${tour?.teamGame || ''} ${tour?.disc || ''} ${tour?.game || ''}`);
export const hasNovaGroups = (tour) => Array.isArray(tour?.groups) && tour.groups.length > 0
  && /dota|cs2|counter|\bcs\b/i.test(`${tour.teamGame || ''} ${tour.disc || ''} ${tour.game || ''} ${tour.hasData || ''}`);

export function teamKey(name) {
  let hash = 2166136261;
  for (const char of name.trim().toLowerCase()) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return `T${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export const DEFAULT_PLAYOFF_SEEDS = ['0:1', '1:2', '1:1', '0:2', '2:1', '3:2', '3:1', '2:2'];

export function playoffSeeds(value = DEFAULT_PLAYOFF_SEEDS) {
  if (!Array.isArray(value) || value.length !== 8 || new Set(value).size !== 8
      || value.some((seed) => !/^[0-3]:[12]$/.test(seed))) {
    throw new Error('В плей-офф каждое место группы должно использоваться ровно один раз.');
  }
  return value;
}

export function buildNovaBracket(groups, results = {}, { preliminary: isPreliminary = false, playoffSeeds: seeds } = {}) {
  if (!Array.isArray(groups) || groups.length !== 4) throw new Error('Для этой сетки нужны ровно четыре группы.');
  const names = {};
  const matches = [];
  const byId = {};
  const slot = (name) => {
    const key = teamKey(name);
    if (names[key]) throw new Error('Названия команд должны быть уникальными во всех группах.');
    names[key] = name;
    return { team: key, label: name };
  };
  const ref = (match, outcome = 'winner') => ({ match: match.id, outcome, label: `${outcome === 'winner' ? 'Победитель' : 'Проигравший'} · ${match.label}` });
  const add = (id, label, aSource, bSource, lane) => {
    const resolve = (source) => source.team || byId[source.match]?.[source.outcome] || null;
    const empty = (source) => source.empty === true || (source.match && byId[source.match]?.[`${source.outcome}Empty`] === true);
    const a = resolve(aSource), b = resolve(bSource);
    const raw = results[id] || {};
    // A result belongs to its original pair. Upstream corrections cannot
    // advance a different team using an old score.
    const valid = a && b && raw.a === a && raw.b === b;
    const result = valid ? raw : {};
    const aEmpty = empty(aSource), bEmpty = empty(bSource);
    const bye = !!((a && bEmpty) || (b && aEmpty));
    const winner = bye ? (a || b) : ([a, b].includes(result.winner) ? result.winner : null);
    const match = { id, label, lane, a, b, aSource, bSource, sa: result.sa ?? '', sb: result.sb ?? '', winner,
      loser: winner ? (winner === a ? b : a) : null, stale: !!raw.winner && !valid,
      bye, winnerEmpty: !!(aEmpty && bEmpty), loserEmpty: !!(aEmpty || bEmpty) };
    matches.push(match);
    byId[id] = match;
    return match;
  };
  const groupBrackets = groups.map((group, gi) => {
    if (!Array.isArray(group.teams) || group.teams.length > 5 || (!isPreliminary && group.teams.length < 2)
        || group.teams.some((name) => typeof name !== 'string' || !name.trim())) {
      throw new Error('В группе нужно от 2 до 5 команд. Для пустых групп или одной команды включите предварительную сетку.');
    }
    const teams = group.teams.map(slot);
    while (teams.length < 4) teams.push({ label: isPreliminary ? 'Команда уточняется' : 'Свободное место', empty: !isPreliminary });
    const prefix = `nova-g${gi}`;
    const start = matches.length;
    const preliminary = teams.length === 5 ? add(`${prefix}-p`, 'Предварительный матч', teams[2], teams[3], 'upper') : null;
    const w1 = add(`${prefix}-w1`, 'Верхняя сетка · 1', teams[0], teams[1], 'upper');
    const w2 = add(`${prefix}-w2`, 'Верхняя сетка · 2', preliminary ? teams[4] : teams[2], preliminary ? ref(preliminary) : teams[3], 'upper');
    const wf = add(`${prefix}-wf`, 'Верхний финал', ref(w1), ref(w2), 'upper');
    const l1 = add(`${prefix}-l1`, 'Нижняя сетка · 1', ref(preliminary || w1, 'loser'), ref(preliminary ? w1 : w2, 'loser'), 'lower');
    const l2 = preliminary ? add(`${prefix}-l2`, 'Нижняя сетка · 2', ref(w2, 'loser'), ref(l1), 'lower') : l1;
    const lf = add(`${prefix}-lf`, 'Нижний финал', ref(l2), ref(wf, 'loser'), 'lower');
    return { ...group, matches: matches.slice(start), first: wf.winner, second: lf.winner, upperFinal: wf, lowerFinal: lf };
  });
  const qualify = (gi, place) => ({ ...ref(place === 1 ? groupBrackets[gi].upperFinal : groupBrackets[gi].lowerFinal), label: `${groups[gi].id} · ${place}-е место` });
  const qualifiers = playoffSeeds(seeds).map((seed) => {
    const [group, place] = seed.split(':').map(Number);
    return qualify(group, place);
  });
  const [q1, q2, q3, q4] = Array.from({ length: 4 }, (_, index) =>
    add(`nova-po-q${index + 1}`, `Четвертьфинал ${index + 1}`, qualifiers[index * 2], qualifiers[index * 2 + 1], 'playoff'));
  const s1 = add('nova-po-s1', 'Полуфинал 1', ref(q1), ref(q2), 'playoff');
  const s2 = add('nova-po-s2', 'Полуфинал 2', ref(q3), ref(q4), 'playoff');
  const final = add('nova-po-f', 'Финал', ref(s1), ref(s2), 'playoff');
  const third = add('nova-po-third', 'Матч за 3-е место', ref(s1, 'loser'), ref(s2, 'loser'), 'third');
  return { groups: groupBrackets, names, matches, playoff: [[q1, q2, q3, q4], [s1, s2], [final]], champion: final.winner, third };
}

export function dependentMatches(matches, id) {
  const affected = new Set([id]);
  for (const match of matches) {
    if ([match.aSource.match, match.bSource.match].some((source) => affected.has(source))) affected.add(match.id);
  }
  return [...affected].filter((matchId) => matchId !== id);
}

// Two parallel matches per hour, with each dependency in an earlier slot.
export function buildNovaSchedule(bracket, overrides = {}) {
  const scheduled = new Set();
  const schedule = [];
  let slot = 0;
  for (const lane of ['upper', 'lower']) {
    for (let gi = 0; gi < bracket.groups.length; gi += 2) {
      const pair = bracket.groups.slice(gi, gi + 2);
      const rounds = pair.map((group) => group.matches.filter((match) => match.lane === lane));
      let pending = Array.from({ length: Math.max(...rounds.map((matches) => matches.length)) }, (_, index) =>
        rounds.flatMap((matches) => matches[index] ? [matches[index]] : [])).flat();
      while (pending.length) {
        const ready = pending.filter((match) => [match.aSource, match.bSource].every((source) => !source.match || scheduled.has(source.match))).slice(0, 2);
        if (!ready.length) throw new Error('Invalid group schedule dependencies');
        const minutes = 9 * 60 + 30 + slot * 60;
        ready.forEach((match, index) => {
          schedule.push({ ...match, group: pair.find((group) => group.matches.some((item) => item.id === match.id)).id,
            day: Math.floor(minutes / 1440) + 1,
            time: `${String(Math.floor(minutes / 60) % 24).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
            stream: index + 1, ...overrides[match.id] });
          scheduled.add(match.id);
        });
        pending = pending.filter((match) => !scheduled.has(match.id));
        slot++;
      }
    }
  }
  // Playoffs start the day after the automatically generated group schedule.
  const playoffDay = Math.max(1, ...schedule.map((match) => match.day)) + 1;
  let playoffSlot = 0;
  for (const round of [...bracket.playoff, [bracket.third]]) {
    for (let index = 0; index < round.length; index += 2) {
      const minutes = 9 * 60 + 30 + playoffSlot * 60;
      round.slice(index, index + 2).forEach((match, court) => {
        schedule.push({ ...match, day: playoffDay + Math.floor(minutes / 1440),
          time: `${String(Math.floor(minutes / 60) % 24).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
          stream: court + 1, ...overrides[match.id] });
      });
      playoffSlot++;
    }
  }
  return schedule.map((match, index) => ({ ...match, order: match.order ?? index })).sort((a, b) => a.order - b.order);
}

// Move a match into another schedule slot, keeping the slot times and courts.
export function moveNovaScheduleMatch(schedule, matchId, position) {
  const ordered = [...schedule];
  const from = ordered.findIndex((match) => match.id === matchId);
  if (from < 0 || position < 0 || position >= ordered.length) return {};
  ordered.splice(position, 0, ordered.splice(from, 1)[0]);
  return Object.fromEntries(ordered.map((match, index) => [match.id, {
    ...(schedule[index].date ? { date: schedule[index].date } : {}),
    ...(match.completed !== undefined ? { completed: match.completed } : {}),
    day: schedule[index].day, time: schedule[index].time,
    stream: schedule[index].stream, order: index,
  }]));
}
